// apps/web/src/api/attachmentsClient.js
import api from '@mysimpledb/shared/config/axios'; // configured (adds Bearer)
import axios from 'axios'; // used for noAuth client
import { toAbsoluteViewUrl } from '@mysimpledb/shared/utils/origin.ts';

// No-auth client: do NOT add Authorization to signed /view or CloudFront requests
const noAuth = axios.create({
  withCredentials: false,
});

/** Upload attachments (auth required) */
export async function uploadAttachments(entryId, files, token) {
  const requestStartedAt = Date.now();
  const fd = new FormData();
  fd.append('entryId', entryId);

  for (const f of files) {
    fd.append('file', f);
  }

  const totalBytes = files.reduce(
    (total, file) => total + Number(file.size || 0),
    0
  );

  let lastLoggedPercent = -1;

  console.log('[upload-client] api-request-start', {
    entryId,
    fileCount: files.length,
    totalBytes,
    files: files.map((file) => ({
      name: file.uploadName || file.name,
      size: Number(file.size || 0),
      type: file.type || '',
    })),
  });

  try {
    const res = await api.post('/attachments/upload', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      onUploadProgress: (event) => {
        const loaded = Number(event.loaded || 0);
        const total = Number(event.total || totalBytes || 0);
        const percent = total > 0 ? Math.round((loaded / total) * 100) : null;

        // Log at roughly 10% intervals to avoid flooding the console.
        if (
          percent == null ||
          percent === 100 ||
          percent >= lastLoggedPercent + 10
        ) {
          lastLoggedPercent = percent ?? lastLoggedPercent;

          console.log('[upload-client] request-upload-progress', {
            entryId,
            loaded,
            total,
            percent,
            elapsedMs: Date.now() - requestStartedAt,
          });
        }
      },
    });

    console.log('[upload-client] api-response-received', {
      entryId,
      status: res.status,
      attachmentCount: res.data?.attachments?.length || 0,
      totalMs: Date.now() - requestStartedAt,
    });

    return res.data;
  } catch (err) {
    console.error('[upload-client] api-request-failed', {
      entryId,
      totalMs: Date.now() - requestStartedAt,
      status: err?.response?.status,
      message: err?.message,
    });

    const data = err?.response?.data || {};

    const friendly = new Error(
      data.error || err.message || 'Attachment upload failed.'
    );

    friendly.code = data.code;
    friendly.status = err?.response?.status;
    friendly.response = err?.response;
    friendly.storageUsedBytes = data.storageUsedBytes;
    friendly.storageLimitBytes = data.storageLimitBytes;
    friendly.incomingBytes = data.incomingBytes;

    throw friendly;
  }
}

/** List attachments for an entry (auth required) */
export async function listAttachments(entryId, token) {
  const res = await api.get(`/attachments/entries/${entryId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return res.data;
}

/**
 * Ask the server for a server-validated signed URL.
 * Returns either:
 * - a relative backend signed /attachments path
 * - or an absolute CloudFront URL
 */
export async function getSignedUrl(
  attachmentId,
  disposition = 'inline',
  token,
  opts = {}
) {
  const { thumb = false } = opts;

  const { data } = await api.post(
    `/attachments/${attachmentId}/signed-url`,
    {
      disposition,
      thumb,
    },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );

  return data?.url || '';
}

/**
 * Fetch the file as a Blob using the signed URL.
 */
export async function fetchBlobByUrl(signedUrl) {
  let url = signedUrl;

  if (!/^https?:\/\//i.test(String(signedUrl || ''))) {
    url = toAbsoluteViewUrl(signedUrl);
  }

  const res = await noAuth.get(url, {
    responseType: 'blob',
    headers: {},
    withCredentials: false,
  });
  return res.data;
}

/** Open in a new tab */
export async function openInNewTab(attachmentId, token) {
  const url = await getSignedUrl(attachmentId, 'inline', token);
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Trigger a download */
export async function downloadAttachment(
  attachmentId,
  filename = 'download',
  token
) {
  const url = await getSignedUrl(attachmentId, 'attachment', token);
  const blob = await fetchBlobByUrl(url);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

/** Optional delete (auth required) */
export async function deleteAttachment(attachmentId, token) {
  const res = await api.delete(`/attachments/${attachmentId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return res.data;
}

/** Update the user-facing attachment display title without renaming the file. */
export async function updateAttachmentDisplayTitle(
  attachmentId,
  displayTitle,
  token
) {
  const { data } = await api.patch(
    `/attachments/${attachmentId}/display-title`,
    { displayTitle },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );

  return data;
}

/** Persist the display order for every attachment in an entry. */
export async function reorderAttachments(entryId, attachmentIds, token) {
  const { data } = await api.patch(
    `/attachments/entries/${entryId}/order`,
    { attachmentIds },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );

  return data;
}

/** Create a link / YouTube attachment (auth required) */
export async function createLinkAttachment(entryId, url, token, title) {
  const kind = /(^https?:\/\/)?(www\.)?(youtu\.be|youtube\.com)\//i.test(url)
    ? 'youtube'
    : 'link';

  const { data } = await api.post(
    '/attachments/link',
    { entryId, url, title, kind },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return data;
}

export async function getDownloadUrl(attachmentId, token) {
  const res = await api.get(`/attachments/${attachmentId}/download-url`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data.url;
}


/** Upload browser-encrypted sensitive attachments. */
export async function uploadSensitiveAttachments(entryId, encryptedItems, token) {
  const fd = new FormData();
  fd.append('entryId', String(entryId));
  fd.append('manifest', JSON.stringify(encryptedItems.map((item) => item.metadata)));
  encryptedItems.forEach((item, index) => {
    fd.append(`original_${index}`, item.originalFile);
    if (item.thumbFile) fd.append(`thumb_${index}`, item.thumbFile);
    if (item.previewFile) fd.append(`preview_${index}`, item.previewFile);
  });
  const res = await api.post('/attachments/sensitive/upload', fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  return res.data;
}

export async function getSensitiveAttachmentInfo(attachmentId, token) {
  const { data } = await api.get(`/attachments/${attachmentId}/sensitive-info`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return data;
}

export async function fetchSensitiveAttachmentCiphertext(attachmentId, kind, token) {
  const { data } = await api.get(
    `/attachments/${attachmentId}/sensitive-object/${kind}`,
    {
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );
  return data;
}
